import { createContext, useContext, useState } from "react";
import { Check, ChevronDown, CircleCheck, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";

/** Um ponto que precisa de resposta antes de aplicar. */
export interface ItemDaRevisao {
  /** id do elemento no formulário: clicar no item rola até ele. */
  id: string;
  rotulo: string;
  detalhe?: string;
  resolvido: boolean;
  /** Sem ação no formulário (ex.: extração incompleta): o revisor marca à mão. */
  onMarcar?: (marcado: boolean) => void;
}

interface Props {
  itens: ItemDaRevisao[];
  /** Razões do classificador que só explicam por que o job veio para revisão. */
  contexto: string[];
  onIr: (id: string) => void;
  modo: "coluna" | "flutuante";
}

/** Onde a coluna do perfil reserva o lugar da checklist (telas largas). */
const SlotDaChecklist = createContext<HTMLElement | null>(null);
export const SlotDaChecklistProvider = SlotDaChecklist.Provider;
export function useSlotDaChecklist() {
  return useContext(SlotDaChecklist);
}

/** O "ok" de um item: cinza enquanto falta, verde quando resolvido. */
function SinalDoItem({ resolvido }: { resolvido: boolean }) {
  return (
    <CircleCheck
      className={cn("h-5 w-5", resolvido ? "fill-green-600 text-white" : "text-muted-foreground/40")}
      aria-hidden
    />
  );
}

/**
 * O que falta para aplicar. Os itens se marcam sozinhos quando o formulário
 * responde; na coluna fica sempre aberta, flutuando começa aberta e recolhe.
 */
export function ChecklistDaRevisao({ itens, contexto, onIr, modo }: Props) {
  const [aberta, setAberta] = useState(true);
  const resolvidos = itens.filter((item) => item.resolvido).length;
  const pronto = resolvidos === itens.length;
  const resumo = itens.length === 0 ? "Nada pendente" : `${resolvidos} de ${itens.length}`;

  const conteudo = (
    <>
      {itens.length > 0 && (
        <ul className="space-y-1">
          {itens.map((item) => (
            <li key={item.id + item.rotulo} className="flex items-start gap-2">
              {item.onMarcar ? (
                <button
                  type="button"
                  onClick={() => item.onMarcar?.(!item.resolvido)}
                  className="shrink-0 rounded-full"
                  aria-pressed={item.resolvido}
                  aria-label={`Marcar como resolvido: ${item.rotulo}`}
                  title={item.resolvido ? "Desmarcar" : "Marcar como resolvido"}
                >
                  <SinalDoItem resolvido={item.resolvido} />
                </button>
              ) : (
                <span className="shrink-0" title={item.resolvido ? "Resolvido" : "Falta responder no formulário"}>
                  <SinalDoItem resolvido={item.resolvido} />
                </span>
              )}
              <button
                type="button"
                onClick={() => onIr(item.id)}
                className="min-w-0 flex-1 text-left text-sm hover:underline"
              >
                <span className={cn(item.resolvido && "text-muted-foreground line-through")}>
                  {item.rotulo}
                </span>
                {item.detalhe && (
                  <span className="block truncate text-xs text-muted-foreground" title={item.detalhe}>
                    {item.detalhe}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {contexto.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">Veio para revisão: {contexto.join(" · ")}</p>
      )}
    </>
  );

  if (modo === "coluna") {
    return (
      <div
        className={cn("mb-4 rounded-lg border bg-background p-3", !pronto && "border-primary ring-1 ring-primary")}
        data-testid="linkedin-revisao-checklist"
      >
        <div className="mb-2 flex items-center justify-between">
          <span className={cn("text-sm font-semibold", !pronto && "text-primary")}>Para aplicar</span>
          <span className={cn("text-xs", pronto ? "text-green-700" : "font-medium text-primary")}>{resumo}</span>
        </div>
        {conteudo}
      </div>
    );
  }

  return (
    <div className="fixed right-4 top-20 z-30 w-72 max-w-[calc(100vw-2rem)]" data-testid="linkedin-revisao-checklist-flutuante">
      {aberta ? (
        <div className={cn("rounded-lg border bg-background p-3 shadow-lg", !pronto && "border-primary ring-1 ring-primary")}>
          <button
            type="button"
            onClick={() => setAberta(false)}
            className="mb-2 flex w-full items-center justify-between"
            aria-label="Recolher a checklist"
          >
            <span className="text-sm font-semibold">Para aplicar</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              {resumo}
              <ChevronDown className="h-4 w-4" />
            </span>
          </button>
          {conteudo}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAberta(true)}
          className={cn(
            "ml-auto flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium shadow-lg",
            pronto
              ? "border bg-background text-green-700 hover:bg-muted"
              : "bg-primary text-primary-foreground hover:bg-primary/90",
          )}
        >
          {pronto ? <Check className="h-4 w-4" /> : <ListChecks className="h-4 w-4" />}
          Para aplicar · {resumo}
          <ChevronDown className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
